import React, { useMemo, useState } from "react";
import { Download, ExternalLink, Filter, GitMerge, LayoutGrid, Link2, List as ListIcon, Mail, MapPin, Phone, Search, Tags, Users } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "../../components/AppLayout";
import { api } from "../../lib/api";
import { STATUSES, STATUS_LABEL, TYPE_HEX, getRecordStatuses, primaryStatus, toggleRecordStatus, typeLabel } from "./constants";
import CrewCleanupPreviewModal from "./CrewCleanupPreviewModal";
import CRMForm from "./CRMForm";
import CRMPreview from "./CRMPreview";
import StudioProfile from "./StudioProfile";
import { EMPTY_FORM } from "./constants";
import { validateRecord } from "./typeFields";
import { getCrewVCardExportRecords, serializeCrewVCards } from "./crewVCard";

const labelFor = (record) => {
  const raw = String(record.stage_name || record.legal_name || "").trim();
  return raw.replace(/^\d+TODO\s*/i, "").trim() || "Unknown contact";
};

const todoCountFor = (record) => {
  const name = String(record.stage_name || "");
  const match = name.match(/^(\d+)TODO\s*/i);
  return match ? Number(match[1]) : Number(record.task_count || 0);
};

const shootCountFor = (record) => Array.isArray(record.shoots) ? record.shoots.length : Number(record.shoot_count || 0);
const hasOpenFollowUp = (record) => (record.follow_ups || []).some((item) => item.follow_up_date && !item.done);

export default function CrewDirectory({ items, onRefresh, onUpdated, onPreview, onEdit, onAdd, onReviewCleanup, title, overline, scopeTypes, canExportVCard = false, loading = false, loadError = null }) {
  const [query, setQuery] = useState("");
  const [role, setRole] = useState("all");
  const [status, setStatus] = useState("all");
  const [position, setPosition] = useState("all");
  const [country, setCountry] = useState("all");
  const [shootRange, setShootRange] = useState("all");
  const [social, setSocial] = useState("all");
  const [contactMethod, setContactMethod] = useState("all");
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState(() => localStorage.getItem("crew_directory_view") || "grid");
  const [selected, setSelected] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkTags, setBulkTags] = useState("");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [vCardExportFeedback, setVCardExportFeedback] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const records = useMemo(() => items.filter((item) => !item.merged_into), [items]);
  const positions = useMemo(() => [...new Set(records.map((item) => item.position).filter(Boolean))].sort(), [records]);
  const countries = useMemo(() => [...new Set(records.map((item) => item.country).filter(Boolean))].sort(), [records]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return records.filter((item) => {
      const statuses = getRecordStatuses(item);
      const socials = [item.twitter, item.instagram, item.onlyfans].filter(Boolean);
      const shootCount = shootCountFor(item);
      const searchable = [item.stage_name, item.legal_name, item.position, item.city, item.country, item.email, item.phone, item.twitter, item.instagram, item.onlyfans, ...(item.tags || [])].join(" ").toLowerCase();
      if (needle && !searchable.includes(needle)) return false;
      if (role !== "all" && item.type !== role) return false;
      if (status !== "all" && !statuses.includes(status)) return false;
      if (position !== "all" && item.position !== position) return false;
      if (country !== "all" && item.country !== country) return false;
      if (shootRange === "zero" && shootCount !== 0) return false;
      if (shootRange === "1-3" && (shootCount < 1 || shootCount > 3)) return false;
      if (shootRange === "4-10" && (shootCount < 4 || shootCount > 10)) return false;
      if (shootRange === "11+" && shootCount < 11) return false;
      if (social === "x" && !item.twitter) return false;
      if (social === "instagram" && !item.instagram) return false;
      if (social === "onlyfans" && !item.onlyfans) return false;
      if (social === "any" && !socials.length) return false;
      if (social === "none" && socials.length) return false;
      if (contactMethod === "phone" && !item.phone) return false;
      if (contactMethod === "email" && !item.email) return false;
      if (contactMethod === "both" && !(item.phone && item.email)) return false;
      if (contactMethod === "missing" && (item.phone || item.email)) return false;
      if (followUpOnly && !hasOpenFollowUp(item)) return false;
      return true;
    }).sort((left, right) => {
      if (sort === "name") return labelFor(left).localeCompare(labelFor(right));
      if (sort === "shoots") return shootCountFor(right) - shootCountFor(left) || labelFor(left).localeCompare(labelFor(right));
      if (sort === "updated") return String(right.updated_at || "").localeCompare(String(left.updated_at || ""));
      return String(right.created_at || "").localeCompare(String(left.created_at || ""));
    });
  }, [records, query, role, status, position, country, shootRange, social, contactMethod, followUpOnly, sort]);

  const countType = (...types) => records.filter((item) => types.includes(String(item.type || "").toLowerCase())).length;
  const countStatus = (value) => records.filter((item) => getRecordStatuses(item).includes(value)).length;

  const setRecordStatus = async () => {
    if (!bulkStatus || !selected.length) return;
    setBusy(true);
    try {
      for (const id of selected) {
        const record = records.find((item) => item.id === id);
        if (!record) continue;
        const current = getRecordStatuses(record);
        const next = current.includes(bulkStatus) ? current : toggleRecordStatus(current, bulkStatus);
        const updated = await api.put(`/api/crm/${id}`, { statuses: next, status: primaryStatus(next) });
        onUpdated(updated.data);
      }
      toast.success(`Updated status for ${selected.length} contacts`);
      setSelected([]);
      setBulkStatus("");
      await onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update all selected contacts");
    } finally {
      setBusy(false);
    }
  };

  const addTags = async () => {
    const tags = bulkTags.split(",").map((tag) => tag.trim()).filter(Boolean);
    if (!tags.length || !selected.length) return;
    setBusy(true);
    try {
      for (const id of selected) {
        const record = records.find((item) => item.id === id);
        if (!record) continue;
        const mergedTags = [...new Set([...(record.tags || []), ...tags])];
        const updated = await api.put(`/api/crm/${id}`, { tags: mergedTags });
        onUpdated(updated.data);
      }
      toast.success(`Added tags to ${selected.length} contacts`);
      setSelected([]);
      setBulkTags("");
      await onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update all selected contacts");
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const columns = ["id", "stage_name", "legal_name", "type", "status", "statuses", "position", "city", "country", "email", "phone", "twitter", "instagram", "onlyfans", "shoot_count", "open_follow_ups", "tags"];
    const escape = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const lines = [columns.map(escape).join(","), ...filtered.map((record) => columns.map((column) => {
      if (column === "shoot_count") return escape(shootCountFor(record));
      if (column === "open_follow_ups") return escape((record.follow_ups || []).filter((item) => item.follow_up_date && !item.done).length);
      return escape(Array.isArray(record[column]) ? record[column].join("; ") : record[column]);
    }).join(","))];
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "crew-contacts.csv";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const exportVCard = () => {
    const exportRecords = getCrewVCardExportRecords(filtered, selected);
    if (!exportRecords.length) {
      const message = "No visible contacts to export";
      setVCardExportFeedback({ type: "error", message });
      toast.error(message);
      return;
    }
    try {
      const blob = new Blob([serializeCrewVCards(exportRecords)], { type: "text/vcard;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "crew-contacts.vcf";
      anchor.click();
      URL.revokeObjectURL(url);
      const label = exportRecords.length === 1 ? "contact" : "contacts";
      const message = `vCard download started for ${exportRecords.length} ${label}.`;
      setVCardExportFeedback({ type: "success", message });
      toast.success(message);
    } catch {
      const message = "Could not create the vCard file";
      setVCardExportFeedback({ type: "error", message });
      toast.error(message);
    }
  };

  const toggleSelected = (id) => setSelected((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  const toggleAll = () => setSelected((current) => filtered.length && filtered.every((item) => current.includes(item.id)) ? current.filter((id) => !filtered.some((item) => item.id === id)) : [...new Set([...current, ...filtered.map((item) => item.id)])]);
  const openPreview = (record) => setPreviewing({ ...record, stage_name: labelFor(record) });
  const openEdit = (record) => {
    setPreviewing(null);
    setEditing(record.id);
    setForm({ ...EMPTY_FORM, ...record, stage_name: labelFor(record) });
  };
  const openAdd = () => {
    setPreviewing(null);
    setEditing("new");
    setForm(EMPTY_FORM);
  };
  const saveRecord = async () => {
    const invalid = validateRecord(form);
    if (invalid) return toast.error(invalid);
    try {
      const response = editing === "new"
        ? await api.post("/api/crm/", form)
        : await api.put(`/api/crm/${editing}`, form);
      onUpdated(response.data);
      setEditing(null);
      setForm(EMPTY_FORM);
      await onRefresh();
      toast.success(editing === "new" ? "Contact added" : "Contact saved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save contact");
    }
  };

  return (
    <AppLayout title={title} overline={overline}>
      <section className="mb-5 grid grid-cols-2 gap-px border md:grid-cols-5 xl:grid-cols-9" style={{ background: "var(--gio-border)", borderColor: "var(--gio-border)" }} data-testid="crew-kpis">
        <Kpi label="Contacts" value={records.length} />
        <Kpi label="Models" value={countType("model", "performer")} />
        <Kpi label="Cameramen" value={countType("cameraman")} />
        <Kpi label="Producers" value={countType("producer")} />
        <Kpi label="Studios / agencies" value={countType("studio")} />
        <Kpi label="New leads" value={countStatus("new_lead")} />
        <Kpi label="Approved" value={countStatus("approved")} />
        <Kpi label="Archived" value={countStatus("archived")} />
        <Kpi label="RIP" value={countStatus("rip")} />
      </section>

      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="crew-directory-toolbar">
        <label className="flex min-w-[220px] flex-1 items-center gap-2 border px-3" style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
          <Search size={15} style={{ color: "var(--gio-muted)" }} />
          <input className="w-full bg-transparent py-2 text-sm outline-none" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search contacts, handles, tags" aria-label="Search contacts" />
        </label>
        <Select label="Role" value={role} onChange={setRole} options={[["all", "All roles"], ...scopeTypes.map((type) => [type, typeLabel(type)])]} />
        <Select label="CRM status" value={status} onChange={setStatus} options={[["all", "All statuses"], ...STATUSES.map((value) => [value, STATUS_LABEL[value] || value.replaceAll("_", " ")])]} />
        <Select label="Position" value={position} onChange={setPosition} options={[["all", "All positions"], ...positions.map((value) => [value, value])]} />
        <Select label="Country" value={country} onChange={setCountry} options={[["all", "All countries"], ...countries.map((value) => [value, value])]} />
        <Select label="Shoot count" value={shootRange} onChange={setShootRange} options={[["all", "Any shoots"], ["zero", "0 shoots"], ["1-3", "1–3 shoots"], ["4-10", "4–10 shoots"], ["11+", "11+ shoots"]]} />
        <Select label="Social links" value={social} onChange={setSocial} options={[["all", "Any social state"], ["any", "Has social"], ["x", "Has X"], ["instagram", "Has Instagram"], ["onlyfans", "Has OnlyFans"], ["none", "No social"]]} />
        <Select label="Phone / email" value={contactMethod} onChange={setContactMethod} options={[["all", "Any contact info"], ["phone", "Has phone"], ["email", "Has email"], ["both", "Has phone + email"], ["missing", "Missing both"]]} />
        <Select label="Sort" value={sort} onChange={setSort} options={[["newest", "Newest"], ["name", "Name A–Z"], ["shoots", "Most shoots"], ["updated", "Recently updated"]]} />
        <label className="inline-flex items-center gap-2 border px-3 py-2 text-xs" style={{ borderColor: "var(--gio-border)", color: "var(--gio-text)" }}>
          <input type="checkbox" checked={followUpOnly} onChange={(event) => setFollowUpOnly(event.target.checked)} /> Has open follow-up
        </label>
        <div className="inline-flex overflow-hidden border" role="group" aria-label="Team Directory view" style={{ borderColor: "var(--gio-border)" }} data-testid="crew-view-toggle">
          <button type="button" onClick={() => { setView("grid"); localStorage.setItem("crew_directory_view", "grid"); }} aria-label="Grid view" aria-pressed={view === "grid"} title="Grid view" data-testid="crew-view-grid" className="p-2" style={{ background: view === "grid" ? "var(--gio-primary-soft)" : "var(--gio-surface)", color: view === "grid" ? "var(--gio-primary)" : "var(--gio-muted)" }}><LayoutGrid size={16} /></button>
          <button type="button" onClick={() => { setView("list"); localStorage.setItem("crew_directory_view", "list"); }} aria-label="List view" aria-pressed={view === "list"} title="List view" data-testid="crew-view-list" className="border-l p-2" style={{ borderColor: "var(--gio-border)", background: view === "list" ? "var(--gio-primary-soft)" : "var(--gio-surface)", color: view === "list" ? "var(--gio-primary)" : "var(--gio-muted)" }}><ListIcon size={16} /></button>
        </div>
        <button type="button" onClick={openAdd} className="btn-primary inline-flex items-center gap-2"><Users size={14} /> Add contact</button>
        <button type="button" onClick={() => onReviewCleanup ? onReviewCleanup() : setReviewOpen(true)} className="btn-ghost inline-flex items-center gap-2"><GitMerge size={14} /> Review duplicates</button>
        <div className="flex w-full flex-wrap items-center gap-2 border-t pt-2 sm:ml-auto sm:w-auto sm:border-t-0 sm:pt-0" style={{ borderColor: "var(--gio-border)" }} role="group" aria-label="Contact exports">
          <button type="button" onClick={exportCsv} className="btn-ghost inline-flex min-h-11 flex-1 items-center justify-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)] sm:flex-none"><Download size={14} aria-hidden="true" /> Export CSV</button>
          {canExportVCard && <button type="button" onClick={exportVCard} disabled={loading || Boolean(loadError) || filtered.length === 0} className="btn-ghost inline-flex min-h-11 flex-1 items-center justify-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gio-primary)] disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none" style={{ borderColor: "var(--gio-primary)", color: "var(--gio-primary)" }} aria-label="Export to Apple Contacts" aria-describedby={!loading && !loadError && filtered.length === 0 ? "crew-directory-empty" : undefined} title={!loading && !loadError && filtered.length === 0 ? "No visible contacts to export" : "Export to Apple Contacts"} data-testid="crew-vcard-export"><Download size={14} aria-hidden="true" /> Export to Apple Contacts</button>}
        </div>
      </div>

      {canExportVCard && vCardExportFeedback && (
        <p
          role={vCardExportFeedback.type === "error" ? "alert" : "status"}
          aria-live={vCardExportFeedback.type === "error" ? "assertive" : "polite"}
          className="mb-3 text-sm"
          style={{ color: vCardExportFeedback.type === "error" ? "var(--gio-danger)" : "var(--gio-success)" }}
          data-testid="crew-vcard-export-status"
        >
          {vCardExportFeedback.message}
        </p>
      )}

      {selected.length > 0 && <div className="mb-3 flex flex-wrap items-center gap-2 border p-3" style={{ borderColor: "var(--gio-primary)", background: "var(--gio-surface)" }}>
        <strong className="mr-2 text-sm">{selected.length} selected</strong>
        <Select label="Bulk CRM status" value={bulkStatus} onChange={setBulkStatus} options={[["", "Change status…"], ...STATUSES.map((value) => [value, STATUS_LABEL[value] || value.replaceAll("_", " ")])]} />
        <button type="button" disabled={busy || !bulkStatus} onClick={setRecordStatus} className="btn-ghost text-xs disabled:opacity-40">Apply status</button>
        <input className="gio-input w-56" value={bulkTags} onChange={(event) => setBulkTags(event.target.value)} placeholder="Add tags, comma separated" aria-label="Tags to add" />
        <button type="button" disabled={busy || !bulkTags.trim()} onClick={addTags} className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-40"><Tags size={13} /> Add tags</button>
        <button type="button" onClick={() => setSelected([])} className="btn-ghost ml-auto text-xs">Clear selection</button>
      </div>}

      <div className="mb-2 flex items-center justify-between text-xs" style={{ color: "var(--gio-muted)" }}>
        <label className="inline-flex items-center gap-2"><input type="checkbox" checked={filtered.length > 0 && filtered.every((item) => selected.includes(item.id))} onChange={toggleAll} /> Select visible</label>
        <span>{filtered.length} visible · {records.length} contacts</span>
      </div>

      <div className={view === "grid" ? "grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" : "divide-y border"} style={{ borderColor: "var(--gio-border)" }} data-testid={view === "grid" ? "crew-directory-grid" : "crew-directory-list"}>
        {loading ? <div role="status" aria-live="polite" className="col-span-full p-10 text-center text-sm" style={{ color: "var(--gio-muted)" }} data-testid="crew-data-load-status">Loading crew directory...</div> : loadError ? <div role="alert" className="col-span-full p-10 text-center text-sm" style={{ color: "var(--gio-danger)" }} data-testid="crew-data-load-error"><p>{loadError}</p><button type="button" onClick={onRefresh} className="btn-ghost mt-3" data-testid="crew-data-load-retry">Retry loading crew directory</button></div> : filtered.length === 0 ? <div id="crew-directory-empty" className="col-span-full p-10 text-center text-sm" style={{ color: "var(--gio-muted)" }} data-testid="crew-directory-empty">No contacts match these filters.</div> : filtered.map((record) => {
          const statuses = getRecordStatuses(record);
          const primary = primaryStatus(statuses);
          const typeColor = TYPE_HEX[record.type] || "var(--gio-primary)";
          const name = labelFor(record);
          const initials = name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
          if (view === "grid") return <CrewContactCard key={record.id} record={record} name={name} initials={initials} typeColor={typeColor} primary={primary} selected={selected.includes(record.id)} onSelect={() => toggleSelected(record.id)} onOpen={() => openPreview(record)} onEdit={() => openEdit(record)} />;
          return <article key={record.id} className="grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-3 p-3 sm:grid-cols-[auto_auto_minmax(0,1fr)_minmax(8rem,0.7fr)_minmax(8rem,0.7fr)_auto]" data-testid={`crew-contact-${record.id}`}>
            <input type="checkbox" checked={selected.includes(record.id)} onChange={() => toggleSelected(record.id)} aria-label={`Select ${name}`} />
            <div className="flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold" style={{ color: typeColor, borderColor: `${typeColor}66`, background: "var(--gio-surface-2)" }}>{initials}</div>
            <div className="min-w-0">
              <button type="button" onClick={() => openPreview(record)} className="block max-w-full truncate text-left text-sm font-semibold hover:underline" style={{ color: "var(--gio-text)" }}>{name}</button>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge color={typeColor}>{typeLabel(record.type)}</Badge>
                <Badge color={primary === "rip" ? "#FF5CB8" : "var(--gio-primary)"}>{STATUS_LABEL[primary] || primary.replaceAll("_", " ")}</Badge>
                {record.position && <Badge>{record.position}</Badge>}
                {todoCountFor(record) > 0 && <Badge>{todoCountFor(record)} tasks</Badge>}
              </div>
            </div>
            <div className={view === "grid" ? "col-span-full min-w-0 text-xs" : "hidden min-w-0 text-xs sm:block"}>
              <div className="flex items-center gap-1 truncate" style={{ color: "var(--gio-muted)" }}><MapPin size={12} /> {[record.city, record.country].filter(Boolean).join(", ") || "Unknown"}</div>
              <div className="mt-1" style={{ color: "var(--gio-muted-3)" }}>{shootCountFor(record)} shoots · {(record.follow_ups || []).filter((item) => item.follow_up_date && !item.done).length} open follow-ups</div>
            </div>
            <div className={view === "grid" ? "col-span-full flex min-w-0 flex-wrap items-center gap-2 text-xs" : "hidden min-w-0 flex-wrap items-center gap-2 text-xs sm:flex"}>
              {record.twitter && <SocialLink href={`https://x.com/${String(record.twitter).replace(/^@/, "")}`} label="X" />}
              {record.instagram && <SocialLink href={`https://instagram.com/${String(record.instagram).replace(/^@/, "")}`} label="IG" />}
              {record.onlyfans && <SocialLink href={`https://onlyfans.com/${String(record.onlyfans).replace(/^@/, "")}`} label="OF" />}
              {record.phone && <Phone size={13} aria-label="Phone available" style={{ color: "var(--gio-muted)" }} />}
              {record.email && <Mail size={13} aria-label="Email available" style={{ color: "var(--gio-muted)" }} />}
              {!record.twitter && !record.instagram && !record.onlyfans && !record.phone && !record.email && <span style={{ color: "var(--gio-muted-3)" }}>No links</span>}
            </div>
            <div className={view === "grid" ? "col-span-full flex items-center justify-end gap-1 border-t pt-2" : "flex items-center gap-1"} style={view === "grid" ? { borderColor: "var(--gio-border)" } : undefined}>
              <button type="button" onClick={() => openPreview(record)} className="btn-ghost p-2" aria-label={`Open ${name}`} title="Open profile"><ExternalLink size={14} /></button>
              <button type="button" onClick={() => openEdit(record)} className="btn-ghost p-2" aria-label={`Edit ${name}`} title="Edit"><Filter size={14} /></button>
            </div>
          </article>;
        })}
      </div>
      {previewing && (previewing.type === "Studio"
        ? <StudioProfile person={previewing} onClose={() => setPreviewing(null)} onEdit={() => openEdit(previewing)} onLocalUpdate={(updated) => { onUpdated(updated); setPreviewing(updated); }} canDelete={false} />
        : <CRMPreview person={previewing} onClose={() => setPreviewing(null)} onEdit={() => openEdit(previewing)} onLocalUpdate={(updated) => { onUpdated(updated); setPreviewing(updated); }} canDelete={false} />)}
      {editing && <CRMForm editing={editing} form={form} setForm={setForm} onClose={() => { setEditing(null); setForm(EMPTY_FORM); }} onSave={saveRecord} />}
      {reviewOpen && <CrewCleanupPreviewModal onClose={() => setReviewOpen(false)} />}
    </AppLayout>
  );
}

function Kpi({ label, value }) {
  return <div className="p-3" style={{ background: "var(--gio-surface-2)" }}><div className="text-[11px]" style={{ color: "var(--gio-muted)" }}>{label}</div><div className="mt-1 font-heading text-xl" style={{ color: "var(--gio-primary)" }}>{value}</div></div>;
}

function Select({ label, value, onChange, options }) {
  return <select aria-label={label} value={value} onChange={(event) => onChange(event.target.value)} className="gio-input max-w-full text-xs">
    {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
  </select>;
}

function Badge({ children, color = "var(--gio-muted)" }) {
  return <span className="border px-1.5 py-0.5 text-[10px] leading-tight" style={{ color, borderColor: "var(--gio-border)" }}>{children}</span>;
}

function SocialLink({ href, label }) {
  return <a href={href} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--gio-primary)" }}><Link2 size={11} />{label}</a>;
}

function CrewContactCard({ record, name, initials, typeColor, primary, selected, onSelect, onOpen, onEdit }) {
  const photo = record.photos?.[0];
  const location = [record.city, record.country].filter(Boolean).join(", ") || "Unknown location";
  const openFollowUps = (record.follow_ups || []).filter((item) => item.follow_up_date && !item.done).length;

  return <article className="group overflow-hidden rounded-lg border transition duration-200 hover:-translate-y-1 hover:border-[color:var(--gio-primary)] hover:shadow-[0_0_18px_rgba(255,45,149,0.2)]" style={{ borderColor: selected ? "var(--gio-primary)" : "rgba(255,45,149,0.2)", background: "rgba(10,10,10,0.72)", boxShadow: selected ? "0 0 14px rgba(255,45,149,0.22)" : "0 8px 24px rgba(0,0,0,0.28)", backdropFilter: "blur(18px)" }} data-testid={`crew-contact-${record.id}`}>
    <div className="relative aspect-[4/5] overflow-hidden" style={{ background: "linear-gradient(145deg, #111111 0%, #080808 55%, rgba(255,45,149,0.13) 100%)" }}>
      {photo ? <img src={photo} alt={name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.04]" /> : (
        <div className="flex h-full w-full items-center justify-center" style={{ color: "var(--gio-primary)", backgroundImage: "linear-gradient(135deg, transparent 49.5%, rgba(255,45,149,0.08) 50%, transparent 50.5%)", backgroundSize: "24px 24px" }}>
          <span className="flex h-20 w-20 items-center justify-center rounded-full border bg-black/35 text-2xl font-bold backdrop-blur-md" style={{ borderColor: "rgba(255,45,149,0.55)", boxShadow: "0 0 24px rgba(255,45,149,0.16)" }}>{initials}</span>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 border-t border-white/10 bg-black/45 p-3 pt-7 backdrop-blur-md">
        <button type="button" onClick={onOpen} className="min-w-0 truncate text-left text-base font-semibold text-white hover:text-[color:var(--gio-primary)]" title={name}>{name}</button>
        <input type="checkbox" checked={selected} onChange={onSelect} aria-label={`Select ${name}`} className="h-4 w-4 shrink-0 accent-pink-500" />
      </div>
    </div>
    <div className="space-y-3 border-t border-white/5 p-3" style={{ background: "rgba(17,17,17,0.58)" }}>
      <div className="flex flex-wrap gap-1.5">
        <GlassBadge color={typeColor}>{typeLabel(record.type)}</GlassBadge>
        <GlassBadge color={primary === "rip" ? "#FF5CB8" : "var(--gio-primary)"}>{STATUS_LABEL[primary] || primary.replaceAll("_", " ")}</GlassBadge>
        {record.position && <GlassBadge>{record.position}</GlassBadge>}
      </div>
      <div className="flex items-start gap-1.5 text-xs" style={{ color: "var(--gio-muted)" }}>
        <MapPin size={13} className="mt-0.5 shrink-0" />
        <span className="min-w-0 truncate" title={location}>{location}</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs" style={{ color: "var(--gio-muted-3)" }}>
        <span>{shootCountFor(record)} shoots</span>
        {openFollowUps > 0 && <span>{openFollowUps} open follow-ups</span>}
      </div>
      <div className="flex min-h-7 flex-wrap items-center gap-1.5 border-t border-white/10 pt-2 text-xs">
        {record.twitter && <SocialLink href={`https://x.com/${String(record.twitter).replace(/^@/, "")}`} label="X" />}
        {record.instagram && <SocialLink href={`https://instagram.com/${String(record.instagram).replace(/^@/, "")}`} label="IG" />}
        {record.onlyfans && <SocialLink href={`https://onlyfans.com/${String(record.onlyfans).replace(/^@/, "")}`} label="OF" />}
        {!record.twitter && !record.instagram && !record.onlyfans && <span style={{ color: "var(--gio-muted-3)" }}>No social links</span>}
        <div className="ml-auto flex items-center gap-1">
          <button type="button" onClick={onOpen} className="btn-ghost p-2" aria-label={`Open ${name}`} title="Open profile"><ExternalLink size={14} /></button>
          <button type="button" onClick={onEdit} className="btn-ghost p-2" aria-label={`Edit ${name}`} title="Edit"><Filter size={14} /></button>
        </div>
      </div>
    </div>
  </article>;
}

function GlassBadge({ children, color = "var(--gio-muted)" }) {
  return <span className="rounded-full border px-2 py-1 text-[10px] leading-tight backdrop-blur-md" style={{ color, borderColor: "rgba(255,45,149,0.22)", background: "rgba(255,255,255,0.035)" }}>{children}</span>;
}